import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { bookingAPI } from '../services/api';
import axios from 'axios';
import { buildApiUrl } from '../config/api';

const BookingTrack = () => {
    const [email, setEmail] = useState('');
    const [bookingId, setBookingId] = useState('');
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchMethod, setSearchMethod] = useState('email');
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [cancellationEmail, setCancellationEmail] = useState('');
    const [payingBookingId, setPayingBookingId] = useState('');

    const normalizeBookingListResponse = (payload) => {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.data)) return payload.data;
        if (Array.isArray(payload?.bookings)) return payload.bookings;
        if (Array.isArray(payload?.data?.bookings)) return payload.data.bookings;
        return [];
    };

    const normalizeSingleBookingResponse = (payload) => {
        if (!payload) return null;
        if (payload?.data && !Array.isArray(payload.data)) return payload.data;
        return payload;
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSelectedBooking(null);

        try {
            if (searchMethod === 'email') {
                const response = await bookingAPI.getCustomerBookings(email);
                const normalizedBookings = normalizeBookingListResponse(response.data);
                setBookings(normalizedBookings);
                if (normalizedBookings.length === 0) {
                    setError('No bookings found with this email address');
                }
            } else {
                const response = await bookingAPI.trackBooking(bookingId);
                const normalizedBooking = normalizeSingleBookingResponse(response.data);
                if (!normalizedBooking) {
                    throw new Error('Booking not found');
                }
                setBookings([normalizedBooking]);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch bookings. Please try again.');
            setBookings([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelBooking = async () => {
        if (!selectedBooking) return;
        const emailForCancellation = cancellationEmail.trim();

        if (!emailForCancellation) {
            alert('Please enter the booking email address to cancel this booking.');
            return;
        }
        
        setCancelling(true);
        try {
            await bookingAPI.cancelBooking(selectedBooking.id, emailForCancellation);
            // Refresh bookings after cancellation
            if (searchMethod === 'email') {
                const response = await bookingAPI.getCustomerBookings(email);
                setBookings(normalizeBookingListResponse(response.data));
            } else {
                const response = await bookingAPI.trackBooking(bookingId);
                const normalizedBooking = normalizeSingleBookingResponse(response.data);
                setBookings(normalizedBooking ? [normalizedBooking] : []);
            }
            setShowCancelModal(false);
            setSelectedBooking(null);
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to cancel booking');
        } finally {
            setCancelling(false);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            confirmed: { bg: '#dcfce7', color: '#166534', icon: 'fa-check-circle' },
            pending: { bg: '#fed7aa', color: '#9a3412', icon: 'fa-clock' },
            completed: { bg: '#dbeafe', color: '#1e40af', icon: 'fa-check-double' },
            cancelled: { bg: '#fee2e2', color: '#991b1b', icon: 'fa-times-circle' }
        };
        return badges[status] || { bg: '#f1f5f9', color: '#475569', icon: 'fa-circle' };
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const getDueAmount = (booking) => {
        return Math.max(Number(booking.total_amount || 0) - Number(booking.advance_amount || 0), 0);
    };

    const handlePayBalance = async (booking) => {
        try {
            setPayingBookingId(booking.booking_id);
            const emailForPayment = searchMethod === 'email' ? email : (cancellationEmail || booking.customer_email || '');

            if (!emailForPayment) {
                window.alert('Booking email is required to pay the balance.');
                return;
            }

            const response = await axios.post(buildApiUrl('/payments/balance/create-order'), {
                booking_id: booking.booking_id,
                customer_email: emailForPayment
            });

            const order = response.data;
            const razorpay = new window.Razorpay({
                key: process.env.REACT_APP_RAZORPAY_KEY_ID,
                amount: order.amount,
                currency: order.currency,
                name: 'RedGoTrip',
                description: `Balance Payment for ${booking.package_title}`,
                order_id: order.orderId,
                prefill: {
                    name: booking.customer_name,
                    email: emailForPayment,
                    contact: booking.customer_phone
                },
                handler: async (paymentResponse) => {
                    await axios.post(buildApiUrl('/payments/balance/verify'), {
                        razorpay_order_id: paymentResponse.razorpay_order_id,
                        razorpay_payment_id: paymentResponse.razorpay_payment_id,
                        razorpay_signature: paymentResponse.razorpay_signature,
                        booking_id: booking.booking_id
                    });

                    if (searchMethod === 'email') {
                        const refreshed = await bookingAPI.getCustomerBookings(email);
                        setBookings(normalizeBookingListResponse(refreshed.data));
                    } else {
                        const refreshed = await bookingAPI.trackBooking(booking.booking_id);
                        const normalizedBooking = normalizeSingleBookingResponse(refreshed.data);
                        setBookings(normalizedBooking ? [normalizedBooking] : []);
                    }
                    window.alert('Balance payment completed successfully.');
                },
                modal: {
                    ondismiss: () => setPayingBookingId('')
                },
                theme: {
                    color: '#1f5ca8'
                }
            });

            razorpay.open();
        } catch (err) {
            console.error('Balance payment failed:', err);
            window.alert(err.response?.data?.message || 'Failed to process balance payment');
        } finally {
            setPayingBookingId('');
        }
    };

    return (
        <div className="booking-track-page">
            {/* Hero Section */}
            <section className="track-hero">
                <div className="hero-pattern"></div>
                <div className="hero-content">
                    <h1>
                        <span className="hero-subtitle">Track Your Journey</span>
                        <span className="hero-title">Booking Status</span>
                    </h1>
                    <p className="hero-description">
                        Enter your email or booking ID to view real-time status of your Kashmir adventure
                    </p>
                </div>
            
            </section>

            <div className="track-container">
                {/* Search Methods */}
                <div className="search-methods-card">
                    <div className="methods-header">
                        <h3>How would you like to track?</h3>
                    </div>
                    <div className="methods-grid">
                        <button 
                            className={`method-card ${searchMethod === 'email' ? 'active' : ''}`}
                            onClick={() => setSearchMethod('email')}
                        >
                            <div className="method-icon">
                                <i className="fas fa-envelope"></i>
                            </div>
                            <div className="method-info">
                                <h4>Search by Email</h4>
                                <p>View all bookings associated with your email</p>
                            </div>
                            {searchMethod === 'email' && (
                                <div className="method-check">
                                    <i className="fas fa-check-circle"></i>
                                </div>
                            )}
                        </button>

                        <button 
                            className={`method-card ${searchMethod === 'id' ? 'active' : ''}`}
                            onClick={() => setSearchMethod('id')}
                        >
                            <div className="method-icon">
                                <i className="fas fa-hashtag"></i>
                            </div>
                            <div className="method-info">
                                <h4>Search by Booking ID</h4>
                                <p>Track a specific booking using its unique ID</p>
                            </div>
                            {searchMethod === 'id' && (
                                <div className="method-check">
                                    <i className="fas fa-check-circle"></i>
                                </div>
                            )}
                        </button>
                    </div>
                </div>

                {/* Search Form */}
                <div className="search-section">
                    <form onSubmit={handleSearch} className="search-form-wrapper">
                        <div className="search-input-group">
                            {searchMethod === 'email' ? (
                                <>
                                    <div className="input-icon">
                                        <i className="fas fa-envelope"></i>
                                    </div>
                                    <input
                                        type="email"
                                        placeholder="Enter your email address"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        className="search-input"
                                    />
                                </>
                            ) : (
                                <>
                                    <div className="input-icon">
                                        <i className="fas fa-hashtag"></i>
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Enter booking ID (e.g., BK1234567890)"
                                        value={bookingId}
                                        onChange={(e) => setBookingId(e.target.value)}
                                        required
                                        className="search-input"
                                    />
                                </>
                            )}
                        </div>
                        <button 
                            type="submit" 
                            className="search-action-btn" 
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    <span>Searching...</span>
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-search"></i>
                                    <span>Track Booking</span>
                                </>
                            )}
                        </button>
                    </form>

                    {/* Recent Searches Hint */}
                    <div className="search-hint">
                        <i className="fas fa-lightbulb"></i>
                        <p>Tip: Your booking ID is in the confirmation email we sent you</p>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="error-alert">
                        <i className="fas fa-exclamation-circle"></i>
                        <p>{error}</p>
                        <button onClick={() => setError(null)} className="error-dismiss">
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                )}

                {/* Loading Skeleton */}
                {loading && (
                    <div className="loading-skeleton">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="skeleton-card">
                                <div className="skeleton-header"></div>
                                <div className="skeleton-body">
                                    <div className="skeleton-line"></div>
                                    <div className="skeleton-line"></div>
                                    <div className="skeleton-line"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Bookings List */}
                {!loading && bookings.length > 0 && (
                    <div className="bookings-section">
                        <div className="bookings-header">
                            <h2>
                                <i className="fas fa-ticket-alt"></i>
                                Your Bookings
                                <span className="booking-count">{bookings.length}</span>
                            </h2>
                            <button 
                                className="clear-search-btn"
                                onClick={() => {
                                    setBookings([]);
                                    setEmail('');
                                    setBookingId('');
                                }}
                            >
                                <i className="fas fa-times"></i>
                                Clear Results
                            </button>
                        </div>

                        <div className="bookings-grid">
                            {bookings.map((booking) => {
                                const statusBadge = getStatusBadge(booking.booking_status);
                                return (
                                    <div key={booking.id} className="booking-card">
                                        {/* Card Header */}
                                        <div className="booking-card-header">
                                            <div className="booking-id-wrapper">
                                                <span className="id-label">Booking ID</span>
                                                <span className="id-value">{booking.booking_id}</span>
                                            </div>
                                            <div 
                                                className="status-badge"
                                                style={{ backgroundColor: statusBadge.bg, color: statusBadge.color }}
                                            >
                                                <i className={`fas ${statusBadge.icon}`}></i>
                                                <span>{booking.booking_status}</span>
                                            </div>
                                        </div>

                                        {/* Card Body */}
                                        <div className="booking-card-body">
                                            <div className="package-info">
                                                <h3>{booking.package_title}</h3>
                                                <div className="package-meta">
                                                    <span>
                                                        <i className="fas fa-calendar-alt"></i>
                                                        {formatDate(booking.travel_date)}
                                                    </span>
                                                    <span>
                                                        <i className="fas fa-users"></i>
                                                        {booking.adults} Adult{booking.adults !== 1 ? 's' : ''}, {booking.children} Child{booking.children !== 1 ? 'ren' : ''}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="booking-details-grid">
                                                <div className="detail-item">
                                                    <span className="detail-label">Customer</span>
                                                    <span className="detail-value">{booking.customer_name}</span>
                                                </div>
                                                {booking.customer_email && (
                                                    <div className="detail-item">
                                                        <span className="detail-label">Email</span>
                                                        <span className="detail-value">{booking.customer_email}</span>
                                                    </div>
                                                )}
                                                {booking.customer_phone && (
                                                    <div className="detail-item">
                                                        <span className="detail-label">Phone</span>
                                                        <span className="detail-value">{booking.customer_phone}</span>
                                                    </div>
                                                )}
                                                <div className="detail-item">
                                                    <span className="detail-label">Total Amount</span>
                                                    <span className="detail-value amount">
                                                        {formatCurrency(booking.total_amount)}
                                                    </span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">Payment Status</span>
                                                    <span className={`payment-badge ${booking.payment_status}`}>
                                                        {booking.payment_status}
                                                    </span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">Payment Method</span>
                                                    <span className="detail-value">{booking.payment_method || 'Online'}</span>
                                                </div>
                                                {getDueAmount(booking) > 0 && (
                                                    <div className="detail-item">
                                                        <span className="detail-label">Balance Due</span>
                                                        <span className="detail-value amount">
                                                            {formatCurrency(getDueAmount(booking))}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            {booking.special_requests && (
                                                <div className="special-requests">
                                                    <i className="fas fa-comment-dots"></i>
                                                    <p>{booking.special_requests}</p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Card Footer */}
                                        <div className="booking-card-footer">
                                            <Link 
                                                to={`/booking/${booking.package_id}`} 
                                                className="footer-action modify"
                                            >
                                                <i className="fas fa-edit"></i>
                                                <span>Modify</span>
                                            </Link>
                                            <button 
                                                className="footer-action cancel"
                                                onClick={() => {
                                                    setSelectedBooking(booking);
                                                    setCancellationEmail(searchMethod === 'email' ? email : '');
                                                    setShowCancelModal(true);
                                                }}
                                                disabled={booking.booking_status === 'cancelled' || booking.booking_status === 'completed'}
                                            >
                                                <i className="fas fa-times-circle"></i>
                                                <span>Cancel</span>
                                            </button>
                                            <Link 
                                                to={`/invoice/${booking.booking_id}`} 
                                                className="footer-action invoice"
                                            >
                                                <i className="fas fa-file-invoice"></i>
                                                <span>Invoice</span>
                                            </Link>
                                            {getDueAmount(booking) > 0 && booking.booking_status !== 'cancelled' && (
                                                <button
                                                    className="footer-action modify"
                                                    onClick={() => handlePayBalance(booking)}
                                                    disabled={payingBookingId === booking.booking_id}
                                                >
                                                    <i className={`fas ${payingBookingId === booking.booking_id ? 'fa-spinner fa-spin' : 'fa-credit-card'}`}></i>
                                                    <span>{payingBookingId === booking.booking_id ? 'Processing' : 'Pay Balance'}</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* No Results State */}
                {!loading && bookings.length === 0 && !error && (
                    <div className="no-results">
                        <div className="no-results-icon">
                            <i className="fas fa-search"></i>
                        </div>
                        <h3>No Bookings Found</h3>
                        <p>Enter your email or booking ID above to track your bookings</p>
                        <div className="help-tips">
                            <h4>Quick Tips:</h4>
                            <ul>
                                <li><i className="fas fa-check"></i> Check your email for the booking confirmation</li>
                                <li><i className="fas fa-check"></i> Booking ID starts with 'BK' followed by numbers</li>
                                <li><i className="fas fa-check"></i> Use the email you provided during booking</li>
                            </ul>
                        </div>
                    </div>
                )}
            </div>

            {/* Cancel Confirmation Modal */}
            {showCancelModal && selectedBooking && (
                <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-icon warning">
                                <i className="fas fa-exclamation-triangle"></i>
                            </div>
                            <h2>Cancel Booking</h2>
                            <button className="modal-close" onClick={() => setShowCancelModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to cancel this booking?</p>
                            <div className="booking-summary">
                                <p><strong>Booking ID:</strong> {selectedBooking.booking_id}</p>
                                <p><strong>Package:</strong> {selectedBooking.package_title}</p>
                                <p><strong>Travel Date:</strong> {formatDate(selectedBooking.travel_date)}</p>
                                <p><strong>Amount:</strong> {formatCurrency(selectedBooking.total_amount)}</p>
                            </div>
                            <div className="cancel-email-field">
                                <label htmlFor="cancellation-email">Booking Email</label>
                                <input
                                    id="cancellation-email"
                                    type="email"
                                    value={cancellationEmail}
                                    onChange={(e) => setCancellationEmail(e.target.value)}
                                    placeholder="Enter booking email address"
                                />
                            </div>
                            <p className="cancellation-note">
                                <i className="fas fa-info-circle"></i>
                                Cancellation charges may apply as per our policy. Refunds will be processed within 7-10 business days.
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn-secondary" 
                                onClick={() => setShowCancelModal(false)}
                                disabled={cancelling}
                            >
                                Keep Booking
                            </button>
                            <button 
                                className="btn-danger" 
                                onClick={handleCancelBooking}
                                disabled={cancelling}
                            >
                                {cancelling ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin"></i>
                                        Cancelling...
                                    </>
                                ) : (
                                    'Yes, Cancel Booking'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .booking-track-page {
                    min-height: 100vh;
                    background: var(--color-page);
                }

                /* Hero Section */
                .track-hero {
                    position: relative;
                    background: linear-gradient(180deg, #ffffff 0%, #3b5382 80%, #334f7f 100%);
                    padding: 5.8rem 2rem 2.4rem;
                    overflow: hidden;
                    border-bottom: 1px solid rgb(15 102 195 / 0.08);
                }

                .hero-pattern {
                    position: absolute;
                    inset: 0;
                    background:
                        radial-gradient(circle at top right, rgb(15 102 195 / 0.12), transparent 24%),
                        radial-gradient(circle at bottom left, rgb(235 93 0 / 0.08), transparent 32%);
                }

                .hero-content {
                    position: relative;
                    max-width: 800px;
                    margin: 0 auto;
                    text-align: center;
                    color: var(--color-text-strong);
                    z-index: 2;
                }

                .hero-subtitle {
                    display: block;
                    font-size: 0.78rem;
                    text-transform: uppercase;
                    letter-spacing: 0.14em;
                    margin-bottom: 1rem;
                    color: var(--color-primary);
                    font-weight: 800;
                }

                .hero-title {
                    display: block;
                    font-size: clamp(2.25rem, 4vw, 3.3rem);
                    font-weight: 800;
                    margin-bottom: 1rem;
                    letter-spacing: -0.035em;
                    line-height: 1.05;
                }

                .hero-description {
                    font-size: 1rem;
                    max-width: 640px;
                    margin: 0 auto;
                    color: var(--color-text-muted);
                    line-height: 1.8;
                }

                .hero-wave {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                }

                .hero-wave svg {
                    width: 100%;
                    height: auto;
                    display: block;
                    fill: #f8fafc;
                }

                /* Container */
                .track-container {
                    max-width: 1200px;
                    margin: -1rem auto 0;
                    padding: 0 2rem 4rem;
                    position: relative;
                    z-index: 10;
                }

                /* Search Methods Card */
                .search-methods-card {
                    background: white;
                    border-radius: 24px;
                    padding: 2rem;
                    box-shadow: var(--shadow-soft);
                    border: 1px solid var(--color-border);
                    margin-bottom: 2rem;
                }

                .methods-header h3 {
                    color: var(--color-text-strong);
                    margin-bottom: 1.5rem;
                    font-size: 1.25rem;
                    letter-spacing: -0.02em;
                }

                .methods-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1rem;
                }

                .method-card {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    padding: 1.5rem;
                    border: 1px solid var(--color-border);
                    border-radius: 18px;
                    background: white;
                    cursor: pointer;
                    transition: all 0.3s;
                    position: relative;
                    text-align: left;
                }

                .method-card:hover {
                    border-color: rgb(15 102 195 / 0.2);
                    transform: translateY(-2px);
                    box-shadow: 0 14px 30px rgb(15 102 195 / 0.08);
                }

                .method-card.active {
                    border-color: rgb(15 102 195 / 0.22);
                    background: linear-gradient(180deg, #f7fbff 0%, #fff 100%);
                    box-shadow: inset 0 0 0 1px rgb(15 102 195 / 0.06);
                }

                .method-icon {
                    width: 50px;
                    height: 50px;
                    background: #eef4fb;
                    border-radius: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: var(--color-primary);
                    font-size: 1.5rem;
                }

                .method-info h4 {
                    color: var(--color-text-strong);
                    margin-bottom: 0.25rem;
                    font-size: 1rem;
                }

                .method-info p {
                    color: var(--color-text-muted);
                    font-size: 0.9rem;
                    margin: 0;
                    line-height: 1.65;
                }

                .method-check {
                    position: absolute;
                    top: 1rem;
                    right: 1rem;
                    color: var(--color-primary);
                    font-size: 1.2rem;
                }

                /* Search Section */
                .search-section {
                    margin-bottom: 2rem;
                }

                .search-form-wrapper {
                    display: flex;
                    gap: 1rem;
                    background: white;
                    border-radius: 24px;
                    padding: 0.5rem;
                    box-shadow: var(--shadow-soft);
                    border: 1px solid var(--color-border);
                }

                .search-input-group {
                    flex: 1;
                    position: relative;
                }

                .input-icon {
                    position: absolute;
                    left: 1.5rem;
                    top: 50%;
                    transform: translateY(-50%);
                    color: var(--color-text-muted);
                    font-size: 1.1rem;
                }

                .search-input {
                    width: 100%;
                    padding: 1.25rem 1.25rem 1.25rem 3.5rem;
                    border: none;
                    border-radius: 18px;
                    font-size: 0.98rem;
                    background: transparent;
                }

                .search-input:focus {
                    outline: none;
                }

                .search-action-btn {
                    padding: 1rem 2rem;
                    background: var(--gradient-accent);
                    color: white;
                    border: none;
                    border-radius: 18px;
                    font-size: 0.95rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.3s;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    white-space: nowrap;
                }

                .search-action-btn:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: var(--shadow-cta);
                }

                .search-action-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .search-hint {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin-top: 1rem;
                    padding: 0.75rem 1rem;
                    background: #f7fbff;
                    border: 1px solid var(--color-border);
                    border-radius: 12px;
                    color: var(--color-text);
                    font-size: 0.9rem;
                }

                .search-hint i {
                    color: var(--color-primary);
                }

                /* Error Alert */
                .error-alert {
                    background: #fff7f5;
                    border: 1px solid #ffd6c8;
                    border-radius: 16px;
                    padding: 1rem 1.5rem;
                    margin-bottom: 2rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    color: #991b1b;
                    position: relative;
                }

                .error-alert i {
                    font-size: 1.2rem;
                }

                .error-alert p {
                    flex: 1;
                    margin: 0;
                }

                .error-dismiss {
                    background: none;
                    border: none;
                    color: #991b1b;
                    cursor: pointer;
                    font-size: 1rem;
                    padding: 0.25rem;
                }

                /* Loading Skeleton */
                .loading-skeleton {
                    display: grid;
                    gap: 1.5rem;
                }

                .skeleton-card {
                    background: white;
                    border-radius: 24px;
                    padding: 1.5rem;
                    box-shadow: var(--shadow-soft);
                    border: 1px solid var(--color-border);
                }

                .skeleton-header {
                    height: 40px;
                    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                    background-size: 200% 100%;
                    animation: loading 1.5s infinite;
                    border-radius: 8px;
                    margin-bottom: 1rem;
                }

                .skeleton-body {
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                }

                .skeleton-line {
                    height: 20px;
                    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                    background-size: 200% 100%;
                    animation: loading 1.5s infinite;
                    border-radius: 4px;
                }

                @keyframes loading {
                    0% { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                }

                /* Bookings Section */
                .bookings-section {
                    margin-top: 2rem;
                }

                .bookings-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1.5rem;
                }

                .bookings-header h2 {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    font-size: 1.5rem;
                    color: var(--color-text-strong);
                }

                .bookings-header h2 i {
                    color: var(--color-primary);
                }

                .booking-count {
                    background: #eef4fb;
                    color: var(--color-primary);
                    padding: 0.25rem 0.75rem;
                    border-radius: 50px;
                    font-size: 0.9rem;
                    margin-left: 0.5rem;
                }

                .clear-search-btn {
                    background: none;
                    border: 1px solid var(--color-border);
                    padding: 0.5rem 1rem;
                    border-radius: 8px;
                    color: var(--color-text-muted);
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    transition: all 0.3s;
                }

                .clear-search-btn:hover {
                    background: #f8fbff;
                    border-color: rgb(15 102 195 / 0.18);
                    color: var(--color-primary);
                }

                .bookings-grid {
                    display: grid;
                    gap: 1.5rem;
                }

                .booking-card {
                    background: white;
                    border-radius: 24px;
                    overflow: hidden;
                    box-shadow: var(--shadow-soft);
                    border: 1px solid var(--color-border);
                    transition: all 0.3s;
                }

                .booking-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 18px 38px rgb(15 102 195 / 0.12);
                }

                .booking-card-header {
                    background: linear-gradient(180deg, #fbfdff 0%, #f7fbff 100%);
                    padding: 1.5rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border-bottom: 1px solid var(--color-border);
                }

                .booking-id-wrapper {
                    display: flex;
                    flex-direction: column;
                }

                .id-label {
                    font-size: 0.8rem;
                    color: var(--color-text-muted);
                }

                .id-value {
                    font-size: 1.1rem;
                    font-weight: 600;
                    color: var(--color-text-strong);
                    font-family: monospace;
                }

                .status-badge {
                    padding: 0.5rem 1rem;
                    border-radius: 50px;
                    font-size: 0.9rem;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .booking-card-body {
                    padding: 1.5rem;
                }

                .package-info h3 {
                    font-size: 1.2rem;
                    color: var(--color-text-strong);
                    margin-bottom: 0.5rem;
                }

                .package-meta {
                    display: flex;
                    gap: 1rem;
                    margin-bottom: 1.5rem;
                }

                .package-meta span {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    color: var(--color-text-muted);
                    font-size: 0.9rem;
                }

                .package-meta i {
                    color: var(--color-primary);
                }

                .booking-details-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 1rem;
                    margin-bottom: 1rem;
                }

                .detail-item {
                    display: flex;
                    flex-direction: column;
                    gap: 0.25rem;
                }

                .detail-label {
                    font-size: 0.8rem;
                    color: #8aa0b6;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .detail-value {
                    font-size: 1rem;
                    color: var(--color-text-strong);
                    font-weight: 500;
                }

                .detail-value.amount {
                    color: var(--color-text-strong);
                    font-weight: 600;
                }

                .payment-badge {
                    display: inline-block;
                    padding: 0.25rem 0.75rem;
                    border-radius: 50px;
                    font-size: 0.8rem;
                    font-weight: 600;
                    text-transform: capitalize;
                }

                .payment-badge.completed {
                    background: #eef4fb;
                    color: var(--color-primary);
                }

                .payment-badge.partial {
                    background: #f4f7fb;
                    color: var(--color-text);
                }

                .payment-badge.pending {
                    background: #f1f5f9;
                    color: var(--color-text-muted);
                }

                .special-requests {
                    display: flex;
                    gap: 0.75rem;
                    padding: 1rem;
                    background: #f8fbff;
                    border: 1px solid var(--color-border);
                    border-radius: 12px;
                    margin-top: 1rem;
                }

                .special-requests i {
                    color: var(--color-primary);
                }

                .special-requests p {
                    color: var(--color-text);
                    font-size: 0.9rem;
                    margin: 0;
                }

                .booking-card-footer {
                    display: flex;
                    border-top: 1px solid var(--color-border);
                    background: #fbfdff;
                }

                .footer-action {
                    flex: 1;
                    padding: 1rem;
                    text-align: center;
                    text-decoration: none;
                    font-size: 0.9rem;
                    font-weight: 500;
                    transition: all 0.3s;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    border: none;
                    background: transparent;
                    cursor: pointer;
                }

                .footer-action.modify {
                    color: var(--color-primary);
                    border-right: 1px solid var(--color-border);
                }

                .footer-action.modify:hover {
                    background: #f3f8fe;
                }

                .footer-action.cancel {
                    color: var(--color-text);
                    border-right: 1px solid var(--color-border);
                }

                .footer-action.cancel:hover:not(:disabled) {
                    background: #f1f5f9;
                }

                .footer-action.cancel:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .footer-action.invoice {
                    color: var(--color-text);
                }

                .footer-action.invoice:hover {
                    background: #f3f8fe;
                }

                /* No Results */
                .no-results {
                    text-align: center;
                    padding: 4rem 2rem;
                    background: white;
                    border-radius: 24px;
                    box-shadow: var(--shadow-soft);
                    border: 1px solid var(--color-border);
                }

                .no-results-icon {
                    width: 100px;
                    height: 100px;
                    background: #eef4fb;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 1.5rem;
                    font-size: 2.5rem;
                    color: var(--color-primary);
                }

                .no-results h3 {
                    color: var(--color-text-strong);
                    margin-bottom: 0.5rem;
                }

                .no-results p {
                    color: var(--color-text-muted);
                    margin-bottom: 2rem;
                }

                .help-tips {
                    max-width: 400px;
                    margin: 0 auto;
                    padding: 1.5rem;
                    background: #f8fbff;
                    border-radius: 16px;
                    border: 1px solid var(--color-border);
                    text-align: left;
                }

                .help-tips h4 {
                    color: var(--color-text-strong);
                    margin-bottom: 1rem;
                }

                .help-tips ul {
                    list-style: none;
                    padding: 0;
                }

                .help-tips li {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    color: var(--color-text);
                    margin-bottom: 0.5rem;
                }

                .help-tips li i {
                    color: var(--color-primary);
                }

                /* Modal */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgb(15 23 42 / 0.38);
                    backdrop-filter: blur(5px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 1rem;
                }

                .modal-content {
                    background: white;
                    border-radius: 24px;
                    max-width: 500px;
                    width: 100%;
                    max-height: 90vh;
                    overflow-y: auto;
                    border: 1px solid var(--color-border);
                    box-shadow: 0 24px 60px rgb(15 23 42 / 0.18);
                }

                .modal-header {
                    padding: 1.5rem;
                    border-bottom: 1px solid var(--color-border);
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    position: relative;
                }

                .modal-icon {
                    width: 50px;
                    height: 50px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.5rem;
                }

                .modal-icon.warning {
                    background: #eef4fb;
                    color: var(--color-primary);
                }

                .modal-header h2 {
                    flex: 1;
                    margin: 0;
                    font-size: 1.3rem;
                    color: var(--color-text-strong);
                }

                .modal-close {
                    background: none;
                    border: none;
                    font-size: 1.2rem;
                    cursor: pointer;
                    color: var(--color-text-muted);
                }

                .modal-body {
                    padding: 1.5rem;
                }

                .booking-summary {
                    background: #f8fbff;
                    padding: 1rem;
                    border-radius: 12px;
                    margin: 1rem 0;
                    border: 1px solid var(--color-border);
                }

                .booking-summary p {
                    margin: 0.5rem 0;
                }

                .cancel-email-field {
                    display: flex;
                    flex-direction: column;
                    gap: 0.45rem;
                    margin: 1rem 0;
                }

                .cancel-email-field label {
                    font-size: 0.9rem;
                    font-weight: 600;
                    color: var(--color-text-strong);
                }

                .cancel-email-field input {
                    width: 100%;
                    padding: 0.85rem 1rem;
                    border-radius: 10px;
                    border: 1px solid var(--color-border);
                    background: #fff;
                    font-size: 0.95rem;
                    color: var(--color-text);
                }

                .cancel-email-field input:focus {
                    outline: none;
                    border-color: var(--color-primary);
                    box-shadow: 0 0 0 3px rgba(236, 49, 52, 0.12);
                }

                .cancellation-note {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.5rem;
                    padding: 1rem;
                    background: #f8fbff;
                    border-radius: 12px;
                    color: var(--color-text);
                    font-size: 0.9rem;
                    border: 1px solid var(--color-border);
                }

                .cancellation-note i {
                    color: var(--color-primary);
                }

                .modal-footer {
                    padding: 1.5rem;
                    border-top: 1px solid var(--color-border);
                    display: flex;
                    gap: 1rem;
                }

                .btn-secondary,
                .btn-danger {
                    flex: 1;
                    padding: 0.75rem;
                    border: none;
                    border-radius: 8px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s;
                }

                .btn-secondary {
                    background: #f5f8fc;
                    color: var(--color-text);
                    border: 1px solid var(--color-border);
                }

                .btn-secondary:hover:not(:disabled) {
                    background: #eef4fb;
                }

                .btn-danger {
                    background: var(--gradient-accent);
                    color: white;
                }

                .btn-danger:hover:not(:disabled) {
                    box-shadow: var(--shadow-cta);
                }

                .btn-danger:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                /* Responsive */
                @media (max-width: 768px) {
                    .hero-title {
                        font-size: 2.5rem;
                    }

                    .methods-grid {
                        grid-template-columns: 1fr;
                    }

                    .search-form-wrapper {
                        flex-direction: column;
                        border-radius: 20px;
                    }

                    .search-action-btn {
                        width: 100%;
                        justify-content: center;
                    }

                    .booking-details-grid {
                        grid-template-columns: 1fr;
                    }

                    .booking-card-footer {
                        flex-direction: column;
                    }

                    .footer-action {
                        border-right: none;
                        border-bottom: 1px solid #e2e8f0;
                    }

                    .footer-action:last-child {
                        border-bottom: none;
                    }

                    .modal-footer {
                        flex-direction: column;
                    }
                }

                @media (max-width: 480px) {
                    .track-container {
                        padding: 0 1rem 3rem;
                    }

                    .hero-title {
                        font-size: 2rem;
                    }

                    .booking-card-header {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 1rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default BookingTrack;
